import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/queries'
import { invoiceFetch as apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { invoiceKeys } from '@/features/invoices/keys'
import {
  USE_MOCK_AP_INVOICES,
  mockInvoiceAssignments,
  mockStaffById,
  applyMockAssignment,
  mockInvoiceDetail,
  getMockInvoice,
} from './mock'
import type {
  ApInvoiceSerialCorrectionInput,
  ApInvoiceCreateInput,
  ApInvoiceUpdateInput,
  ApInvoiceArchiveInput,
  ApInvoiceBulkUpdateInput,
} from './types'
import type { ApInvoiceDetail, ApInvoiceBulkUpdateResult } from './view-models'

export function useCreateInvoice() {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ApInvoiceCreateInput) => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          ...mockInvoiceDetail(),
          ...body,
          id: 9999,
          uuid: 'inv-demo-created',
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceDetail>(token, '/dashboard/ap-invoices', {
        method: 'POST',
        json: body,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
    },
  })
}

export function useUpdateInvoice(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ApInvoiceUpdateInput) => {
      if (USE_MOCK_AP_INVOICES) {
        const current = getMockInvoice(uuid)
        if ('assigned_user_id' in body || 'status' in body || 'priority_status' in body) {
          const assignedUser =
            'assigned_user_id' in body ? mockStaffById(body.assigned_user_id) : undefined
          mockInvoiceAssignments.set(uuid, {
            ...(mockInvoiceAssignments.get(uuid) ?? {}),
            ...('status' in body && body.status != null ? { status: body.status } : {}),
            ...('priority_status' in body ? { priority_status: body.priority_status } : {}),
            ...('assigned_user_id' in body
              ? {
                  assigned_user_id: assignedUser?.id ?? null,
                  assigned_user: assignedUser,
                  assigned_source: assignedUser ? 'manual' : null,
                }
              : {}),
          })
          return applyMockAssignment({
            ...current,
            ...body,
          } as ApInvoiceDetail)
        }
        return { ...current, ...body }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceDetail>(token, `/dashboard/ap-invoices/${uuid}`, {
        method: 'PATCH',
        json: body,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      })
    },
  })
}

export function useCorrectInvoiceSerial(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      serialUuid,
      body,
    }: {
      serialUuid: string
      body: ApInvoiceSerialCorrectionInput
    }) => {
      if (USE_MOCK_AP_INVOICES) {
        return (
          queryClient.getQueryData<ApInvoiceDetail>(invoiceKeys.detail(uuid)) ?? mockInvoiceDetail()
        )
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceDetail>(
        token,
        `/dashboard/ap-invoices/${uuid}/equipment-serials/${serialUuid}`,
        { method: 'PATCH', json: body },
      )
    },
    onMutate: async ({ serialUuid, body }) => {
      const detailKey = invoiceKeys.detail(uuid)
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<ApInvoiceDetail>(detailKey)
      if (previous) {
        queryClient.setQueryData<ApInvoiceDetail>(detailKey, {
          ...previous,
          lines: previous.lines?.map((line) => ({
            ...line,
            serials: line.serials.map((serial) =>
              serial.uuid === serialUuid
                ? {
                    ...serial,
                    ...(body.serial_number !== undefined
                      ? {
                          serial_raw: body.serial_number,
                          serial_normalized: body.serial_number
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, ''),
                        }
                      : {}),
                    ...(body.purchase_order_line_item_id !== undefined
                      ? {
                          purchase_order_line_item_id: body.purchase_order_line_item_id,
                          purchase_order_line_item: null,
                          internal_order_line_item_id: body.internal_order_line_item_id ?? null,
                          internal_order_line_item: null,
                        }
                      : {}),
                    ...(body.internal_order_line_item_id !== undefined
                      ? {
                          internal_order_line_item_id: body.internal_order_line_item_id,
                          internal_order_line_item: null,
                        }
                      : {}),
                    ...(body.review_status ? { review_status: body.review_status } : {}),
                    ...(body.match_confidence !== undefined
                      ? { match_confidence: body.match_confidence }
                      : {}),
                    protected_from_automation: true,
                  }
                : serial,
            ),
          })),
        })
      }
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(invoiceKeys.detail(uuid), context.previous)
      }
    },
    onSuccess: (invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(uuid), invoice)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(uuid) })
    },
  })
}

export function useBulkUpdateInvoices() {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ uuids, ...patch }: ApInvoiceBulkUpdateInput) => {
      if (!uuids.length) throw new Error('Select at least one invoice')
      const updateBody: Omit<ApInvoiceBulkUpdateInput, 'uuids'> = {}
      if (patch.mark_paid_in_full) updateBody.mark_paid_in_full = true
      if (patch.status !== undefined) updateBody.status = patch.status
      if (patch.priority_status !== undefined) updateBody.priority_status = patch.priority_status
      if (patch.assigned_user_id !== undefined) updateBody.assigned_user_id = patch.assigned_user_id
      if (!Object.keys(updateBody).length) {
        throw new Error('Choose mark paid, status, priority, or assignee update')
      }

      if (USE_MOCK_AP_INVOICES) {
        const updated: string[] = []
        for (const uuid of uuids) {
          const nextAssignment =
            patch.assigned_user_id === undefined
              ? {}
              : {
                  assigned_user_id: patch.assigned_user_id,
                  assigned_user: mockStaffById(patch.assigned_user_id),
                  assigned_source: patch.assigned_user_id == null ? null : 'manual',
                }
          mockInvoiceAssignments.set(uuid, {
            ...(mockInvoiceAssignments.get(uuid) ?? {}),
            ...(patch.mark_paid_in_full ? { status: 'paid', priority_status: null } : {}),
            ...(patch.status !== undefined ? { status: patch.status } : {}),
            ...(patch.priority_status !== undefined
              ? { priority_status: patch.priority_status }
              : {}),
            ...nextAssignment,
          })
          updated.push(uuid)
        }
        return { updated, failed: [] } satisfies ApInvoiceBulkUpdateResult
      }

      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceBulkUpdateResult>(token, '/dashboard/ap-invoices/bulk-update', {
        method: 'POST',
        json: { uuids, ...updateBody },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useArchiveInvoice(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ApInvoiceArchiveInput) => {
      const reason = body.reason?.trim() || null
      if (USE_MOCK_AP_INVOICES) {
        return {
          ...getMockInvoice(uuid),
          archived_at: new Date().toISOString(),
          archive_reason: reason,
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceDetail>(token, `/dashboard/ap-invoices/${uuid}/archive`, {
        method: 'POST',
        json: { reason },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      })
    },
  })
}
